import { scheduledQuotesBlotterApiConnector } from 'apiConnector/scheduledRun/scheduledQuotesBlotter';
import { isNavigatingInScheduledQuotesBlotter } from 'epic/utils/navigation/navigation';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, interval, of, zip } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  ignoreElements,
  map,
  mergeMap,
  skipUntil,
  startWith,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import type { Action } from 'state/actions';
import type { IScheduledQuotesBlotterFilters } from 'state/blotters/scheduledQuotes';
import {
  getBlotterRequest,
  initialScheduledQuoteBlotterFilters,
  scheduledQuotesBlotterColumnDefinitionFailed,
  scheduledQuotesBlotterColumnDefinitionReceived,
  scheduledQuotesBlotterFiltersLoaded,
  scheduledQuotesBlotterFiltersLoadingFailed,
  scheduledQuotesBlotterItemsFetchingFailed,
  scheduledQuotesBlotterItemsReceived,
  scheduledQuotesBlotterItemsReloaded,
  scheduledQuotesBlotterItemsRequested,
  scheduledQuotesBlotterPreferenceStateGet,
} from 'state/blotters/scheduledQuotes';
import type { EpicWithThunk } from 'state/state.common';

export function fetchColumnDefinitionEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      ofType('SCHEDULED_QUOTES_BLOTTER_FILTERS_LOADED'),
      mergeMap(_ => {
        return scheduledQuotesBlotterApiConnector(httpClient)
          .fetchColumnDefinition()
          .pipe(
            mergeMap(columns =>
              of(scheduledQuotesBlotterColumnDefinitionReceived(columns), scheduledQuotesBlotterPreferenceStateGet()),
            ),
            catchError(() => {
              return of(scheduledQuotesBlotterColumnDefinitionFailed());
            }),
          );
      }),
    );
}

export function persistFilters(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      localStorageApiConnector,
      selectors: {
        scheduledQuotesBlotter: { getFilters },
      },
    },
  ) =>
    action$.pipe(
      ofType('SCHEDULED_QUOTES_BLOTTER_FILTER_CHANGED'),
      withLatestFrom(state$),
      switchMap(([, state]) => {
        const filters = getFilters(state);
        return localStorageApiConnector('scheduled-quotes-blotter-filters', initialScheduledQuoteBlotterFilters)
          .save<IScheduledQuotesBlotterFilters>(filters)
          .pipe(
            ignoreElements(),
            catchError(() => {
              return EMPTY;
            }),
          );
      }),
    );
}

export function loadFilters(): EpicWithThunk {
  return (actions$, _state$, { localStorageApiConnector }) =>
    zip(
      actions$.pipe(ofType('USER_CURRENT_RECEIVED'), take(1)),
      actions$.pipe(filter(isNavigatingInScheduledQuotesBlotter), take(1)),
    ).pipe(
      switchMap(_ => {
        return localStorageApiConnector('scheduled-quotes-blotter-filters', initialScheduledQuoteBlotterFilters)
          .load<IScheduledQuotesBlotterFilters>()
          .pipe(
            mergeMap(response => [scheduledQuotesBlotterFiltersLoaded(response)]),
            catchError(() => {
              return of(scheduledQuotesBlotterFiltersLoadingFailed());
            }),
          );
      }),
    );
}

function isNavigatingOut(action: Action) {
  return action.type === 'ROUTE_CHANGED' && action.path !== '/scheduled-quotes-blotter';
}

export function pollItemsEpic(pollingFrequency: number = 10_000, scheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { selectors: { isInScheduledQuoteBlotterRoute }, isPageVisible }) => {
    const navigationOut$: Observable<Action> = action$.pipe(filter(isNavigatingOut));

    return action$.pipe(
      filter(
        action =>
          action.type === 'SCHEDULED_QUOTES_BLOTTER_ITEMS_RECEIVED' || isNavigatingInScheduledQuotesBlotter(action),
      ),
      withLatestFrom(state$),
      filter(([, state]) => isInScheduledQuoteBlotterRoute(state)),
      switchMap(([, state]) =>
        interval(pollingFrequency, scheduler).pipe(
          filter(() => {
            return isInScheduledQuoteBlotterRoute(state) && isPageVisible();
          }),
          takeUntil(navigationOut$),
        ),
      ),
      map(() => scheduledQuotesBlotterItemsReloaded()),
    );
  };
}

export function fetchItemsEpic(aggregationFrequency: number = 500, scheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { httpClient, frontLogger }) => {
    return action$.pipe(
      ofType(
        'SCHEDULED_QUOTES_BLOTTER_ITEMS_RELOADED',
        'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_LOADED',
        'SCHEDULED_QUOTES_BLOTTER_FILTER_CHANGED',
        'SCHEDULED_RUN_RESCHEDULE_SUCCEEDED',
        'SCHEDULED_RUN_PAUSE_SCHEDULE_SUCCEEDED',
        'SCHEDULED_RUN_UPDATE_QUOTES_SUCCEEDED',
        'SCHEDULED_RUN_UPDATE_SCHEDULE_SUCCEEDED',
        'SCHEDULED_RUN_DELETE_SCHEDULE_SUCCEEDED',
        'SCHEDULED_RUN_ADD_TO_SCHEDULE_SUCCEEDED',
      ),
      skipUntil(action$.pipe(filter(({ type }) => type === 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_LOADED'))),
      debounceTime(aggregationFrequency, scheduler),
      withLatestFrom(state$),
      switchMap(([, state]) => {
        const request = getBlotterRequest(state);
        return scheduledQuotesBlotterApiConnector(httpClient)
          .search(request)
          .pipe(
            map(res => {
              return (
                res ?? {
                  pageInfo: { page: 0, size: 0, totalElements: 0, totalPages: 0 },
                  entries: [],
                }
              );
            }),
            map(({ entries, pageInfo }) =>
              scheduledQuotesBlotterItemsReceived(entries, pageInfo.totalElements, pageInfo.totalPages),
            ),
            startWith(scheduledQuotesBlotterItemsRequested()),
            catchError(err => {
              frontLogger.error('scheduled quote blotter failed to fetch items: {@FrontError}', err);
              return of(scheduledQuotesBlotterItemsFetchingFailed(err.message));
            }),
          );
      }),
    );
  };
}
